import React, { useEffect, useState } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  AttachedTable,
  Resource,
  ResourceFeature,
  UpgradableNumber
} from "@dndModels";
import { ModelBase } from "@models";
import { LEVELED_SPELL_LEVELS, REFRESH_RULES } from "@dndModels/Constants";
import { SpellLevel } from "@dndModels/Types";

import DandDAPI from "@dndUtilities/system_api";

import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";

import SelectResourceBox from "@dndComponents/select/SelectResourceBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import StringBox from "@components/input/strings/StringBox";
import CheckBox from "@components/input/CheckBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import ModelBaseInput from "@dndComponents/ModelBaseInput";

interface Props {
  obj: ResourceFeature;
  onChange: (changed: ResourceFeature) => void; 
  disabled: boolean;
  attached_table?: AttachedTable;
}

const api = DandDAPI.getInstance();

export default function ResourceFeatureInput({
  obj,
  onChange,
  disabled,
  attached_table
}: Props) {
  const [ping, setPing] = useState(false);
  const [open, setOpen] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [new_resource, setNewResource] = useState(new Resource());

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  // const submit = () => {
  //   setProcessing(true);
  //   // debugger;
  //   api.upsertObject("resource", new_resource).then((res: any) => {
  //     // debugger;
  //     setProcessing(false);
  //     if (res && res.body) {
  //       const new_obj = obj;
  //       new_obj.type_id = res.body.id;
  //       onChange(new_obj);
  //     }
  //     setReloading(true);
  //     setOpen(false);
  //   });
  // }

  const renderExtras = () => {
    return (
      <ModalOrDrawer
        open={ open } 
        onClose={() => { setOpen(false); }}>
        <ModelBaseInput 
          obj={new_resource}
          onChange={(obj: ModelBase) => {
            if (obj instanceof Resource) {
              setNewResource(obj);
            }
          }} 
          onSubmit={submit}
          disabled={false}
          processing={processing}
          onCancel={() => { setOpen(false); }}
        />
      </ModalOrDrawer>
    );
  };

  const submit = () => {
    setProcessing(true);
    // debugger;
    api.upsertObject("resource", new_resource).then((res: any) => {
      // debugger;
      setProcessing(false);
      if (res && res.body) {
        const new_obj = obj;
        new_obj.type_id = res.body.id;
        onChange(new_obj);
      }
      setReloading(true);
      setOpen(false);
    });
  }

  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 6
        }}>
        <CheckBox 
          name="Slots" 
          value={obj.slots} 
          disabled={disabled}
          onChange={(changed: boolean) => {
            const new_obj = obj;
            new_obj.slots = changed;
            onChange(new_obj);
            setPing(!ping);
          }} 
        />
      </Grid>
      { obj.slots ?
        <>
          <Grid
            size={{
              xs: 3
            }}>
            <SelectObjectBox 
              name="Slot Type" 
              data_type="spell_slot_type"
              display_format="{obj.true_name}"
              value={obj.type_id} 
              disabled={disabled}
              onChange={(changed: string) => {
                const new_obj = obj;
                new_obj.type_id = changed;
                onChange(new_obj);
                setPing(!ping);
              }} 
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <SelectStringBox 
              name="Slot Level" 
              options={LEVELED_SPELL_LEVELS}
              extra_options={["Use Size"]}
              value={ obj.slot_level === -1 ? "Use Size" : `${obj.slot_level}`} 
              disabled={disabled}
              onChange={(changed: string) => {
                const new_obj = obj;

                new_obj.slot_level = changed === "Use Size" ? -1 : (+changed) as SpellLevel;
                onChange(new_obj);
                setPing(!ping);
              }} 
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Slot Name"
              value={obj.slot_name}
              disabled={disabled}
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.slot_name = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
        </>
      :
        <Grid
          size={{
            xs: 6
          }}>
          { !reloading && 
            <SelectResourceBox 
              name="Resource Type" 
              value={obj.type_id} 
              extra_options={["Add New Resource Type"]}
              disabled={disabled}
              onChange={(changed: string) => {
                if (changed === "Add New Resource Type") {
                  const nr = new Resource();
                  if (api.user && api.user.admin) {
                    api.applyWorkingOn(nr);
                  }
                  nr.name = obj.name;
                  nr.description = obj.description;
                  setNewResource(nr);
                  setOpen(true);
                  setReloading(true);
                } else {
                  const new_obj = obj;
                  new_obj.type_id = changed;
                  onChange(new_obj);
                  setPing(!ping);
                }
              }} 
            />
          }
        </Grid>
      }
      <Grid
        size={{
          xs: 6
        }}>
        <StringBox
          name="Override Name"
          value={obj.override_name}
          onBlur={(changed: string) => {
            const new_obj = obj;
            new_obj.override_name = changed;
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <StringBox
          name="Override Singular"
          value={obj.override_singular}
          onBlur={(changed: string) => {
            const new_obj = obj;
            new_obj.override_singular = changed;
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <UpgradableNumberBox 
          name="Total"
          value={obj.total} 
          disabled={disabled}
          attached_table={attached_table}
          onChange={(value: UpgradableNumber) => {
            const new_obj = obj;
            new_obj.total = value;
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <UpgradableNumberBox 
          name="Size"
          value={obj.size} 
          disabled={disabled}
          // log_it
          attached_table={attached_table}
          onChange={(value: UpgradableNumber) => {
            const new_obj = obj;
            new_obj.size = value;
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <SelectStringBox 
          options={[...REFRESH_RULES,"Inherit"]} 
          value={obj.refresh_rule}
          name="Refresh Rule"
          onChange={(changed: string) => {
            const new_obj = obj;
            new_obj.refresh_rule = changed;
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
      { obj.refresh_rule === "Long Rest" &&
        <Grid
          size={{
            xs: 12
          }}>
          <UpgradableNumberBox  
            name="Refresh on Short Rest"
            value={obj.refresh_x_on_short}
            attached_table={attached_table}
            onChange={(value: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.refresh_x_on_short = value.clone();
              onChange(new_obj);
              setPing(!ping);
            }}
          />
        </Grid>
      }
      <Grid
        size={{
          xs: 12
        }}>
        <ToggleButtonBox 
          name="Stacks"
          value={obj.stacks} 
          disabled={disabled}
          onToggle={() => {
            const new_obj = obj;
            new_obj.stacks = !new_obj.stacks;
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
      { renderExtras() }
    </Grid>
  );
}
